import React, { Component } from 'react'
import PropTypes from 'prop-types'
import campaignsService from 'services/campaigns'

// Keep only the fields widget_data needs; the campaign-sync on the backend
// ignores the rest and audit columns would otherwise go stale on the widget.
export const toStoredCampaign = full => ({
  id: full.id,
  name: full.name,
  topicId: full.topicId == null ? null : full.topicId,
  posts: (full.posts || []).map(post => ({
    id: post.id,
    label: post.label,
    postId: post.postId,
    permalink: post.permalink
  }))
})

/**
 * Pulse Intelligence: Campaigns card. Pick an existing campaign or create a new
 * one; the choice persists to widget_data.intentBased.campaign. The post
 * registry (ordering, permalinks) is a separate card; this card owns only the
 * campaign identity, preserving any posts loaded from an existing campaign.
 *
 * React 15 has no hooks, so this is a class component; the campaigns service is
 * injectable for tests via the campaignsSvc prop.
 */
class CampaignsCard extends Component {
  constructor (props) {
    super(props)
    const campaign = props.campaign || null
    this.state = {
      campaigns: [],
      useNewCampaign: !(campaign && campaign.id),
      nameDraft: (campaign && campaign.name) || '',
      selectedId: (campaign && campaign.id) || '',
      loading: false,
      loadError: ''
    }
    // Only the latest selection is applied, so a slow load cannot overwrite a
    // newer choice; the name edit flag stops the mount switching to the
    // dropdown after the user has started typing a new name.
    this.selectionSeq = 0
    this.nameTouched = false
  }

  componentDidMount () {
    this.loadCampaigns(this.props.ownerId)
  }

  // ownerId and campaign arrive asynchronously: a new widget gets its owner
  // after mount, and an edit widget reinitialises both via getWidget. Re-fetch
  // when the owner changes and resync the dropdown when the saved campaign
  // lands, so the existing-campaign selection is never left empty.
  componentDidUpdate (prevProps) {
    if (prevProps.ownerId !== this.props.ownerId) {
      this.loadCampaigns(this.props.ownerId)
      return
    }
    const prevId = (prevProps.campaign && prevProps.campaign.id) || ''
    const nextId = (this.props.campaign && this.props.campaign.id) || ''
    if (prevId !== nextId) this.syncSelectedCampaign(this.state.campaigns)
  }

  loadCampaigns (ownerId) {
    if (!ownerId) {
      this.setState({ campaigns: [] })
      return
    }
    this.props.campaignsSvc
      .fetchBrandCampaigns(ownerId)
      .then(list => {
        const loaded = Array.isArray(list) ? list : []
        this.setState({ campaigns: loaded })
        this.syncSelectedCampaign(loaded)
      })
      .catch(() => this.setState({ campaigns: [] }))
  }

  syncSelectedCampaign (campaigns) {
    const { campaign } = this.props
    const match = campaign && campaigns.find(c => c.id === campaign.id)
    if (match && !this.nameTouched) {
      this.setState({ useNewCampaign: false, selectedId: match.id })
    }
  }

  writeCampaign (next) {
    this.props.onChange(next)
  }

  handleToggle (useNew) {
    this.setState({ useNewCampaign: useNew, loadError: '' })
    if (useNew) {
      // Cancel any in-flight selection so a late response cannot overwrite the
      // new campaign or leave a stale load error behind.
      this.selectionSeq++
      this.setState({ loading: false, selectedId: '' })
      const name = this.state.nameDraft.trim()
      const campaign = this.props.campaign || {}
      this.writeCampaign(name ? { ...campaign, id: null, name } : null)
    } else {
      this.setState({ nameDraft: '' })
      this.writeCampaign(null)
    }
  }

  handleNameChange (value) {
    this.nameTouched = true
    this.setState({ nameDraft: value })
    const name = value.trim()
    const campaign = this.props.campaign || {}
    // Keep the campaign and any registered posts while the name is briefly
    // blank; discarding a campaign is an explicit toggle, not a blank name.
    const hasPosts = (campaign.posts || []).length > 0
    this.writeCampaign(
      name || hasPosts ? { ...campaign, id: null, name } : null
    )
  }

  handleSelect (id) {
    this.setState({ selectedId: id, loadError: '' })
    if (!id) {
      this.writeCampaign(null)
      return
    }
    const seq = ++this.selectionSeq
    this.setState({ loading: true })
    this.props.campaignsSvc
      .fetchCampaign(id)
      .then(full => {
        if (seq !== this.selectionSeq) return
        this.setState({ loading: false })
        this.writeCampaign(toStoredCampaign(full))
      })
      .catch(() => {
        if (seq !== this.selectionSeq) return
        this.setState({
          loading: false,
          loadError: 'Could not load the selected campaign. Try again.'
        })
      })
  }

  render () {
    const {
      campaigns,
      useNewCampaign,
      nameDraft,
      selectedId,
      loading,
      loadError
    } = this.state

    return (
      <div className='form-group darkPanel'>
        <div className='col-md-12'>
          <div className='portlet light'>
            <div className='portlet-title'>
              <div className='caption'>
                <span className='caption-subject bold font-green-haze uppercase'>
                  <i
                    className='glyphicon glyphicon-screenshot hidden-xs'
                    style={{ marginRight: 8 }}
                  />
                  Pulse Intelligence: Campaigns
                </span>
              </div>
            </div>
            <div className='portlet-body'>
              <div className='form-group row'>
                <div className='col-sm-12'>
                  <p className='help-block'>
                    A campaign groups the posts that belong to one activity so
                    the system can reason across them. Pick an existing campaign
                    or create a new one. Leave blank if this widget is not part
                    of a campaign.
                  </p>
                </div>
              </div>

              <div className='form-group row'>
                <div className='col-sm-12'>
                  <label
                    style={{
                      marginRight: 24,
                      fontWeight: 'normal',
                      cursor: 'pointer'
                    }}
                  >
                    <input
                      type='radio'
                      id='brt-pulse-campaign-new'
                      name='pulseCampaignMode'
                      checked={useNewCampaign}
                      onChange={() => this.handleToggle(true)}
                      style={{ marginRight: 6 }}
                    />
                    Create a new campaign
                  </label>
                  <label style={{ fontWeight: 'normal', cursor: 'pointer' }}>
                    <input
                      type='radio'
                      id='brt-pulse-campaign-existing'
                      name='pulseCampaignMode'
                      checked={!useNewCampaign}
                      onChange={() => this.handleToggle(false)}
                      style={{ marginRight: 6 }}
                    />
                    Use an existing campaign
                  </label>
                </div>
              </div>

              {useNewCampaign ? (
                <div className='form-group row'>
                  <label className='col-sm-3 col-md-3 col-lg-2 control-label'>
                    Campaign name
                  </label>
                  <div className='col-sm-9 col-md-9 col-lg-10'>
                    <input
                      id='brt-pulse-campaign-name'
                      type='text'
                      className='form-control'
                      autoComplete='off'
                      placeholder='e.g. Black Friday 2026'
                      value={nameDraft}
                      onChange={e => this.handleNameChange(e.target.value)}
                    />
                  </div>
                </div>
              ) : (
                <div className='form-group row'>
                  <label className='col-sm-3 col-md-3 col-lg-2 control-label'>
                    Campaign
                  </label>
                  <div className='col-sm-9 col-md-9 col-lg-10'>
                    <select
                      id='brt-pulse-campaign-select'
                      className='form-control'
                      value={selectedId}
                      disabled={loading}
                      onChange={e => this.handleSelect(e.target.value)}
                    >
                      <option value=''>Select a campaign</option>
                      {campaigns.map(c => (
                        <option key={c.id} value={c.id}>
                          {c.name}
                        </option>
                      ))}
                    </select>
                    {loading && (
                      <span className='help-block'>Loading campaign…</span>
                    )}
                    {loadError && (
                      <span className='help-block font-red'>{loadError}</span>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    )
  }
}

CampaignsCard.propTypes = {
  ownerId: PropTypes.string,
  campaign: PropTypes.object,
  onChange: PropTypes.func.isRequired,
  campaignsSvc: PropTypes.object
}

CampaignsCard.defaultProps = {
  campaignsSvc: campaignsService
}

export default CampaignsCard



// WEBPACK FOOTER //
// ./src/pages/widgets/form/configure-pulse-intelligence/campaigns-card.js